<template>
    <component
        :is="is"
        v-if="show"
        :data="data"
        :has-eager-image="hasEagerImage"
        v-bind="bind"
    />
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent } from 'vue'
import type {
    Color,
    ComponentFragment,
    ShopWithConfidenceProductFragment,
} from '@/graphql/api'
import { assertNever } from '@/helpers'
import type { Props as RelatedProductProps } from '@/components/rings/RelatedProducts.vue'

const ComponentContent = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentContent.vue')
)
const CardComponent = defineAsyncComponent(
    () => import('@/components/cms/component/CardComponent.vue')
)
const ComponentTextOverImage = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentTextOverImage.vue')
)
const ComponentRoundedImage = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentRoundedImage.vue')
)
const ComponentRoundedImageContent = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentRoundedImageContent.vue')
)
const ComponentReviewCarousel = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentReviewCarousel.vue')
)
const ComponentCustomProductCarousel = defineAsyncComponent(
    () =>
        import('@/components/cms/component/ComponentCustomProductCarousel.vue')
)
const ComponentMainCover = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentMainCover.vue')
)
const DebugComponent = defineAsyncComponent(
    () => import('@/components/cms/component/DebugComponent.vue')
)
const ComponentVideo = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentVideo.vue')
)
const ComponentVideoWithContent = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentVideoWithContent.vue')
)
const ComponentInstagramPosts = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentInstagramPosts.vue')
)
const ComponentLogos = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentLogos.vue')
)
const ComponentLogosCarousel = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentLogosCarousel.vue')
)
const ComponentTextWithImage = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentTextWithImage.vue')
)
const ComponentGalleryCarousel = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentGalleryCarousel.vue')
)
const ComponentCardCarousel = defineAsyncComponent(
    () =>
        import(
            '@/components/cms/component/carousel/card/ComponentCardCarousel.vue'
        )
)
const ComponentFullWidthImage = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentFullWidthImage.vue')
)
const ComponentSideBySideImages = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentSideBySideImages.vue')
)
const ComponentArticlePreviews = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentArticlePreviews.vue')
)
const ComponentInstagramWithContent = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentInstagramWithContent.vue')
)
const ComponentBannerTitle = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentBannerTitle.vue')
)
const ComponentQuote = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentQuote.vue')
)
const ComponentBannerSkinny = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentBannerSkinny.vue')
)
const ComponentGlobalSpacer = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentGlobalSpacer.vue')
)
const ComponentTextWithImageCarousel = defineAsyncComponent(
    () =>
        import('@/components/cms/component/ComponentTextWithImageCarousel.vue')
)
const ComponentContentWithGallery = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentContentWithGallery.vue')
)
const ComponentIconCarousel = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentIconCarousel.vue')
)
const ComponentFaq = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentFaq.vue')
)
const ComponentContentTwoColumns = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentContentTwoColumns.vue')
)
const ComponentTable = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentTable.vue')
)
const ComponentArticleGrid = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentArticleGrid.vue')
)
const ComponentOnThisPage = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentOnThisPage.vue')
)
const ComponentCatalogueProductCarousel = defineAsyncComponent(
    () =>
        import(
            '@/components/cms/component/ComponentCatalogueProductCarousel.vue'
        )
)
const RelatedProducts = defineAsyncComponent(
    () => import('@/components/rings/RelatedProducts.vue')
)
const ComponentGlobalComponent = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentGlobalComponent.vue')
)
const ComponentTextCarousel = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentTextCarousel.vue')
)
const ComponentGalleryWithEnquiry = defineAsyncComponent(
    () =>
        import(
            '@/components/cms/component/gallery-with-enquiry/ComponentGalleryWithEnquiry.vue'
        )
)
const ComponentSquareBoxes = defineAsyncComponent(
    () =>
        import(
            '@/components/cms/component/square-boxes/ComponentSquareBoxes.vue'
        )
)
const ComponentShopWithConfidence = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentShopWithConfidence.vue')
)
const ComponentWarrantyActivation = defineAsyncComponent(
    () =>
        import('@/components/cms/component/ComponentWarrantyActivationForm.vue')
)
const ComponentJobs = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentJobs.vue')
)

const ComponentLogosWithText = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentLogosWithText.vue')
)

const props = defineProps<{
    data: ComponentFragment
    background: Color
    hasEagerImage?: boolean
    relatedProducts?: RelatedProductProps
    product?: ShopWithConfidenceProductFragment
}>()

const show = computed(() => {
    const data = props.data

    switch (data.__typename) {
        case 'ComponentShopWithConfidence':
            return !!props.product
        case 'ComponentRelatedProducts':
            return !!props.relatedProducts?.products.length
        default:
            return true
    }
})

const bind = computed((): Record<string, unknown> => {
    const data = props.data

    switch (data.__typename) {
        case 'ComponentContent':
        case 'ComponentVideoWithContent':
        case 'ComponentTextWithImage':
        case 'ComponentTextWithImageCarousel':
        case 'ComponentInstagramWithContent':
        case 'ComponentIconCarousel':
        case 'ComponentTextCarousel':
        case 'ComponentContentWithGallery':
            return {
                background: props.background,
            }
        case 'ComponentShopWithConfidence':
            return {
                background: props.background,
                product: props.product,
            }
        case 'ComponentRelatedProducts':
            return props.relatedProducts || {}
        case 'ComponentGlobalComponent':
            return {
                relatedProducts: props.relatedProducts,
                product: props.product,
            }
        default:
            return {}
    }
})

const hasEagerImage = computed((): boolean => {
    const data = props.data

    switch (data.__typename) {
        case 'ComponentArticleGrid':
        case 'ComponentArticlePreviews':
        case 'ComponentCardCarousel':
        case 'ComponentContent':
        case 'ComponentContentWithGallery':
        case 'ComponentFullWidthImage':
        case 'ComponentGalleryCarousel':
        case 'ComponentInstagramPosts':
        case 'ComponentInstagramWithContent':
        case 'ComponentRoundedImageContent':
        case 'ComponentRoundedImage':
        case 'ComponentSideBySideImages':
        case 'ComponentTextOverImage':
        case 'ComponentTextWithImage':
        case 'ComponentTextWithImageCarousel':
        case 'ComponentVideoWithContent':
        case 'ComponentSquareBoxes':
            return props.hasEagerImage
        default:
            return false
    }
})

const is = computed(() => {
    const data = props.data

    switch (data.__typename) {
        case 'ComponentContent':
            return ComponentContent
        case 'ComponentCard':
            return CardComponent
        case 'ComponentCustomProductCarousel':
            return ComponentCustomProductCarousel
        case 'ComponentCatalogueProductCarousel':
            return ComponentCatalogueProductCarousel
        case 'ComponentReviewCarousel':
            return ComponentReviewCarousel
        case 'ComponentRoundedImage':
            return ComponentRoundedImage
        case 'ComponentRoundedImageContent':
            return ComponentRoundedImageContent
        case 'ComponentTextOverImage':
            return ComponentTextOverImage
        case 'ComponentMainCover':
            return ComponentMainCover
        case 'ComponentVideo':
            return ComponentVideo
        case 'ComponentVideoWithContent':
            return ComponentVideoWithContent
        case 'ComponentInstagramPosts':
            return ComponentInstagramPosts
        case 'ComponentLogos':
            return ComponentLogos
        case 'ComponentLogosCarousel':
            return ComponentLogosCarousel
        case 'ComponentTextWithImage':
            return ComponentTextWithImage
        case 'ComponentGalleryCarousel':
            return ComponentGalleryCarousel
        case 'ComponentCardCarousel':
            return ComponentCardCarousel
        case 'ComponentFullWidthImage':
            return ComponentFullWidthImage
        case 'ComponentSideBySideImages':
            return ComponentSideBySideImages
        case 'ComponentArticlePreviews':
            return ComponentArticlePreviews
        case 'ComponentInstagramWithContent':
            return ComponentInstagramWithContent
        case 'ComponentBannerTitle':
            return ComponentBannerTitle
        case 'ComponentBannerSkinny':
            return ComponentBannerSkinny
        case 'ComponentQuote':
            return ComponentQuote
        case 'ComponentGlobalSpacer':
            return ComponentGlobalSpacer
        case 'ComponentTextWithImageCarousel':
            return ComponentTextWithImageCarousel
        case 'ComponentContentWithGallery':
            return ComponentContentWithGallery
        case 'ComponentIconCarousel':
            return ComponentIconCarousel
        case 'ComponentTextCarousel':
            return ComponentTextCarousel
        case 'ComponentFaq':
            return ComponentFaq
        case 'ComponentContentTwoColumns':
            return ComponentContentTwoColumns
        case 'ComponentTable':
            return ComponentTable
        case 'ComponentArticleGrid':
            return ComponentArticleGrid
        case 'ComponentOnThisPage':
            return ComponentOnThisPage
        case 'ComponentRelatedProducts':
            return RelatedProducts
        case 'ComponentGlobalComponent':
            return ComponentGlobalComponent
        case 'ComponentGalleryWithEnquiry':
            return ComponentGalleryWithEnquiry
        case 'ComponentSquareBoxes':
            return ComponentSquareBoxes
        case 'ComponentShopWithConfidence':
            return ComponentShopWithConfidence
        case 'ComponentWarrantyActivationForm':
            return ComponentWarrantyActivation
        case 'ComponentJobs':
            return ComponentJobs
        case 'ComponentLogosWithText':
            return ComponentLogosWithText
        default:
            return assertNever(data, DebugComponent)
    }
})
</script>
